let csrfToken = ''

export async function initCsrf(): Promise<void> {
  const res = await fetch('/api/antiforgery/token', { credentials: 'include' })
  if (res.ok) {
    const data = await res.json() as { token: string }
    csrfToken = data.token
  }
}

/** One entry of a 409 duplicate-detection response's `candidates` array (see
 * `DuplicateResponses.cs` on the backend — entity type + id + which field
 * matched only, deliberately no PII). */
export interface DuplicateCandidate {
  entityType: string
  id: number
  matchedField: string
}

/**
 * Thrown by `api()` instead of a plain `Error` when the response is a 409 whose
 * body has the shape produced by the backend's duplicate-detection conflict
 * response (`{ message, candidates: [...] }`). Callers that care can
 * `instanceof`-check for this to render a real "possible duplicate" UI instead
 * of the generic error message; everyone else can keep treating it as a plain
 * `Error` (it still carries `.message`).
 */
export class DuplicateConflictError extends Error {
  readonly candidates: DuplicateCandidate[]

  constructor(message: string, candidates: DuplicateCandidate[]) {
    super(message)
    this.name = 'DuplicateConflictError'
    this.candidates = candidates
  }
}

function isDuplicateCandidateArray(value: unknown): value is DuplicateCandidate[] {
  return Array.isArray(value) && value.every(c => {
    if (c === null || typeof c !== 'object') return false
    const rec = c as Record<string, unknown>
    return typeof rec['entityType'] === 'string' &&
      typeof rec['matchedField'] === 'string' &&
      'id' in rec
  })
}

/**
 * F-040 / REQ-032-002: the MFA endpoints (`Api/AuthController.cs`, `Api/UsersController.cs`) all
 * return a stable machine-readable `code` alongside `detail` on failure (e.g. `MFA_INVALID_CODE`,
 * `MFA_NOT_ACTIVE`) — see `Application/MfaAuditActions.Motivations`. `api()` below attaches it to
 * the thrown `Error` when present, additively: every existing catch site that only reads
 * `.message` (the overwhelming majority — verified by reading this file's other callers) sees no
 * change, since `ApiError extends Error`.
 */
export class ApiError extends Error {
  // `string | undefined` spelled out, not `code?: string` — this project's
  // `exactOptionalPropertyTypes` would otherwise reject assigning the constructor's (implicitly
  // `string | undefined`) optional parameter straight to the field below.
  readonly code: string | undefined
  /** F-074: the parsed error body (e.g. the `{ blocking, warnings }` of a 422), when there is one. */
  readonly body: unknown
  /** F-158: lo status HTTP della risposta (es. 403), per riconoscere l'esito senza leggere il messaggio. */
  readonly status: number | undefined

  constructor(message: string, code?: string, body?: unknown, status?: number) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.body = body
    this.status = status
  }
}

/**
 * F-063 (REQ-226-002): chiave per l'header `Idempotency-Key`. Una per "tentativo di creazione" (un
 * `useRef` nel form, rigenerato dopo un successo): il doppio clic o il retry dopo un timeout riusano la
 * stessa chiave e il server non crea un doppione; un nuovo inserimento ne usa una nuova.
 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

/** Opzioni di `api()` con l'header `Idempotency-Key` (solo se la chiave e' presente). */
export function withIdempotencyKey(options: RequestInit, key: string | undefined): RequestInit {
  if (!key) return options
  const headers = new Headers(options.headers)
  headers.set('Idempotency-Key', key)
  return { ...options, headers }
}

/**
 * F-109 (REQ-184-001): quando il server risponde 428 `CHANGE_REASON_REQUIRED` (flag `Audit:RequireChangeReason`
 * acceso e azione marcata) `api()` chiede il motivo con questa funzione e ripete la richiesta con l'header
 * `X-Change-Reason` (codificato URL, il server lo decodifica). Ritorna `null` se l'utente annulla: allora
 * `api()` lancia un `ApiError`. Montata una volta in App da `ChangeReasonDialog`.
 */
export type ChangeReasonPrompt = (message?: string) => Promise<string | null>

let changeReasonPrompt: ChangeReasonPrompt | null = null

export function setChangeReasonPrompt(fn: ChangeReasonPrompt | null): void {
  changeReasonPrompt = fn
}

export const CHANGE_REASON_REQUIRED = 'CHANGE_REASON_REQUIRED'
export const CHANGE_REASON_CANCELLED = 'CHANGE_REASON_CANCELLED'
export const CHANGE_REASON_MAX_LENGTH = 500

export async function api<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const isWrite = options.method && options.method !== 'GET'

  const send = (reason?: string) => {
    const headers = new Headers(options.headers)
    headers.set('Content-Type', 'application/json')
    if (isWrite && csrfToken) {
      headers.set('X-XSRF-TOKEN', csrfToken)
    }
    if (reason) headers.set('X-Change-Reason', encodeURIComponent(reason))
    return fetch(url, {
      ...options,
      credentials: 'include',
      headers,
    })
  }

  let res = await send()

  // F-109: 428 = serve una motivazione. Si chiede una sola volta e si ripete la richiesta con l'header.
  if (res.status === 428 && isWrite && changeReasonPrompt && !new Headers(options.headers).has('X-Change-Reason')) {
    const body = await res.clone().json().catch(() => null) as { code?: string; message?: string } | null
    if (body?.code === CHANGE_REASON_REQUIRED) {
      const reason = await changeReasonPrompt(body.message)
      if (reason === null || reason.trim() === '') {
        throw new ApiError('Operazione annullata: motivazione non fornita.', CHANGE_REASON_CANCELLED, body)
      }
      res = await send(reason.trim().slice(0, CHANGE_REASON_MAX_LENGTH))
    }
  }

  if (res.status === 204) return undefined as T
  if (!res.ok) {
    const err = await res.json().catch(() => ({ title: res.statusText })) as
      { title?: string; message?: string; error?: string; detail?: string; candidates?: unknown; code?: string }

    // TASK-0126: DuplicateConflictError's body always comes from the backend's
    // DuplicateResponses.cs (Customers and Leads modules), which is gated above by the
    // `candidates` array shape and deliberately only ever emits `message` (never `title`,
    // never `error` — see that file's own doc comments). No `err.error` fallback needed here.
    if (res.status === 409 && isDuplicateCandidateArray(err.candidates)) {
      throw new DuplicateConflictError(
        err.title ?? err.message ?? `HTTP ${res.status}`,
        err.candidates,
      )
    }

    // `err.title` is unused by any backend endpoint today (kept first as a no-cost forward
    // compat slot), `err.message` is the shape used by 7 sites (incl. DuplicateResponses.cs),
    // `err.error` is the dominant shape (46 sites across 17 controllers), and `err.detail`
    // (added here) is used by conflict responses (409) in LeadsController for call-attempt
    // uniqueness violations. Ordering is safe: this fix is purely additive — every existing
    // title/message/error consumer keeps seeing exactly what it saw before.
    throw new ApiError(
      err.title ?? err.message ?? err.error ?? err.detail ?? `HTTP ${res.status}`,
      typeof err.code === 'string' ? err.code : undefined,
      err,
      res.status,
    )
  }
  return res.json() as Promise<T>
}

export interface Page<T> {
  items: T[]
  total: number
  page: number
  size: number
}
